import { v4 as uuid } from 'uuid';
import { Component, EventEmitter, Input, Output } from '@angular/core';
import { ControlContainer, UntypedFormControl, NgForm } from '@angular/forms';
import { Observable } from 'rxjs';
import { debounceTime, filter, mergeMap } from 'rxjs/operators';
import { ApiClientConstant } from '@cureskin/api-client';
import { ConnectionService } from '../../services/connection-service';
import { AppConfig } from '../../app/app.config';

@Component({
    selector: 'regimen-selection',
    templateUrl: './regimen-selection.html',
    viewProviders: [{ provide: ControlContainer, useExisting: NgForm }],
    standalone: false
})
export class RegimenSelectionComponent {
  @Input('regimenId')
  set onUpdateRegimenId(regimenId: string) {
    this.updateRegimenByRegimenId(regimenId);
  }
  @Input('name')
  set onUpdateFieldName(name: string) {
    this.name = name;
  }
  @Input('type') type: Array<string> = [ApiClientConstant.Regimen.Type.MAIN, ApiClientConstant.Regimen.Type.ALTERNATE];
  @Input('required') required: boolean = false;
  @Input('placeholder') placeholder: string = '';
  @Input('filter') filter: Record<string, unknown> = {};
  @Input('disabled')
  set onUpdateDisabled(status: any) {
    this.disabled = !!status;
  }
  disabled: boolean = false;
  @Output('onValueChange') onValueChange: EventEmitter<any> = new EventEmitter();

  skipUpdate: boolean = false;
  name: string = uuid();
  autoCompleteProductController: UntypedFormControl = new UntypedFormControl();
  regimenOptions: Observable<Array<{ name: string; object: any }>>;
  regimen: any;
  regimenId: string;

  constructor(private conn: ConnectionService, private appConfig: AppConfig) {
  }

  async ngOnInit(): Promise<any> {
    this.regimenOptions = this.autoCompleteProductController.valueChanges.pipe(
      debounceTime(300),
      filter((token: string) => !!token.length),
      mergeMap((token: string) => this.getRegimens(token)));
  }

  async getRegimens(name: string): Promise<Array<{ name: string; object: any }>> {
    const products = await this.conn.findRegimens({
      where: {
        regimenId: { $regex: name, $options: 'i' },
        type: this.type,
        ...this.filter,
      },
      limit: 10,
      project: ['regimenId'],
      ascending: 'regimenId',
    });
    return products.map((object: any): { name: string; object: any } => ({
      name: object.get('regimenId'),
      object,
    }));
  }

  async autoCompleteRegimenOnSelect(item: { name: string; object: any }): Promise<any> {
    this.setRegimen(item.object);
    this.notifyChange();
  }

  private async updateRegimenByRegimenId(regimenId: string): Promise<void> {
    if (!regimenId || regimenId === this.regimenId) {
      return;
    }
    const regimens = await this.conn.findRegimens({ where: { regimenId }, limit: 1, project: ['regimenId'] });
    this.skipUpdate = true;
    this.setRegimen(regimens[0]);
  }

  private setRegimen(regimen: any): void {
    this.regimen = regimen;
    this.regimenId = regimen.get('regimenId');
  }

  reset(): void {
    delete this.regimen;
    delete this.regimenId;
    this.notifyChange();
  }

  private notifyChange(): void {
    if (!this.skipUpdate) {
      this.onValueChange.emit(this.regimen);
    }
    delete this.skipUpdate;
  }
}
